import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import LinkIcon from "@mui/icons-material/Link";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CircularProgress from "@mui/material/CircularProgress";
import { usePropertyContext } from "../../../../contexts/PropertyContext";
import { supabase } from "../../../../utils/supabaseClient";

function isValidUrl(input) {
  try {
    const parsed = new URL(input.trim());
    return /^https?:$/.test(parsed.protocol);
  } catch {
    return false;
  }
}

export default function LinkHouseModal({ open, onClose }) {
  const [tab, setTab] = useState(0);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { addProperty } = usePropertyContext();


  // jomi: vibecode disto, nuno muda como quiseres
  // mantem so a logica: mandas url, recebes property id
  const handleSubmit = async () => {
    if (!url.trim()) {
      setError("Please enter a URL.");
      return;
    }
    if (!isValidUrl(url)) {
      setError("Please enter a valid URL.");
      return;
    }
    setError("");
    setLoading(true);

    try {
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Please login to add properties");
      }

      const response = await fetch("http://localhost:5001/api/scrape", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ url: url.trim() }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to process URL");
      }

      // Fetch the property data from Supabase using the returned ID
      const { data: propertyData, error: propertyError } = await supabase
        .from("estates")
        .select("*")
        .eq("estate_id", result.estate_id)
        .single();

      if (propertyError || !propertyData) {
        throw new Error("Failed to fetch property details");
      }

      // Add the property to the context
      await addProperty(propertyData);

      onClose();
      setUrl("");
    } catch (err) {
      setError(err.message || "Failed to process the URL. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUrl("");
    setError("");
    setTab(0);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 0,
          bgcolor: "#f8fafc",
          boxShadow: 8,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 0,
          fontWeight: 700,
          fontSize: 22,
        }}
      >
        {tab === 0 ? (
          <>
            <LinkIcon color="primary" sx={{ fontSize: 28, mb: 0.2 }} />
            Link a New House
          </>
        ) : (
          <>
            <EditNoteIcon color="primary" sx={{ fontSize: 28, mb: 0.2 }} />
            Add Manually
          </>
        )}
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        centered
        sx={{ mb: 0, mt: 1, borderBottom: 1, borderColor: "divider" }}
        TabIndicatorProps={{ style: { height: 3, borderRadius: 2 } }}
      >
        <Tab
          iconPosition="start"
          icon={<LinkIcon sx={{ mr: 1, fontSize: 18 }} />}
          label={
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              Link
            </span>
          }
          sx={{
            minWidth: 80,
            fontWeight: 600,
            fontSize: 14,
            minHeight: 32,
            height: 32,
            py: 0,
          }}
        />
        <Tab
          iconPosition="start"
          icon={<EditNoteIcon sx={{ mr: 1, fontSize: 18 }} />}
          label={
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              Add Manually
            </span>
          }
          sx={{
            minWidth: 80,
            fontWeight: 600,
            fontSize: 14,
            minHeight: 32,
            height: 32,
            py: 0,
          }}
        />
      </Tabs>
      {tab === 0 ? (
        <>
          <DialogContent sx={{ pt: 2, pb: 1, px: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="House URL"
              type="url"
              fullWidth
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              error={!!error}
              helperText={error}
              placeholder="https://..."
              sx={{
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
            <Box sx={{ mt: 1.5, color: "text.secondary", fontSize: 14 }}>
              Paste the link of the house you found on a real estate website
              (e.g. Imovirtual, Idealista).
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
            <Button
              onClick={handleClose}
              color="secondary"
              sx={{ borderRadius: 2, fontWeight: 500, mr: 3 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ borderRadius: 2, fontWeight: 600, px: 3, minWidth: 100 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Link"}
            </Button>
          </DialogActions>
        </>
      ) : (
        <DialogContent
          sx={{
            pt: 4,
            pb: 4,
            px: 3,
            textAlign: "center",
            color: "text.secondary",
            fontSize: 16,
          }}
        >
          Manual input form coming soon...
        </DialogContent>
      )}
    </Dialog>
  );
}
